<script setup lang="ts">
  import { CustomCheckbox, FieldSearchBar, SelectionSection } from '@/shared/ui';
  import type { TrainingMatrixDocumentSectionState } from '../model/useTrainingMatrixDocumentSections';
  import type { TrainingMatrixSectionSearchField } from '@/entities/Training';

  interface Props {
    section: TrainingMatrixDocumentSectionState;
    sectionIndex: number;
  }

  defineProps<Props>();

  const emit = defineEmits<{
    search: [index: number, term: string];
    'update:search-field': [index: number, field: TrainingMatrixSectionSearchField];
    'update:selected-items': [index: number, ids: string[]];
  }>();

  const searchFields = [
    { id: 'prefixo', name: 'Prefixo' },
    { id: 'titulo', name: 'Título' },
  ];
</script>

<template>
  <div
    :data-testid="`training-matrix-document-section-${section.prefixCode}`"
    class="border-b border-gray-02 px-6 pb-8"
  >
    <h3 class="text-sm font-semibold text-gray-07 mb-3">{{ section.title }}</h3>

    <FieldSearchBar
      :search-field-id="section.searchField"
      :search-fields="searchFields"
      :initial-search-term="section.search"
      independent-search-field
      class="mb-6"
      @update:search-field-id="
        (value) => emit('update:search-field', sectionIndex, value as TrainingMatrixSectionSearchField)
      "
      @search="(term) => emit('search', sectionIndex, term)"
    />

    <SelectionSection
      :title="section.title"
      hide-selection
      collapse-when-hidden
      fit-content
      radius-class="rounded-2xl"
      padding-class="pl-6 pr-3 py-6"
      count-text-class="mt-3 pl-4 text-sm text-gray-07 text-left"
      select-all-label=""
      :items="section.items"
      :selected-items="section.selectedItems"
      :count="section.selectedItems.length"
      count-label="Total de Documentos selecionados:"
      @update:selected-items="(ids) => emit('update:selected-items', sectionIndex, ids)"
    >
      <template #columns>
        <th class="text-left font-semibold pb-4">Prefixo</th>
        <th class="text-left font-semibold pb-4">Título</th>
      </template>

      <template #item="{ item }">
        <td class="pb-3 flex gap-3 items-center">
          <CustomCheckbox
            :id="`section-${section.prefixCode}-document-${item.documentRevisionId}`"
            :value="String(item.documentRevisionId)"
            :checked="section.selectedItems.includes(String(item.documentRevisionId))"
            @change="
              () => {
                const id = String(item.documentRevisionId);
                const next = section.selectedItems.includes(id)
                  ? section.selectedItems.filter((selectedId) => selectedId !== id)
                  : [...section.selectedItems, id];
                emit('update:selected-items', sectionIndex, next);
              }
            "
          />
          {{ item.fullPrefix }}
        </td>
        <td class="pb-3">{{ item.documentName }}</td>
      </template>
    </SelectionSection>

    <p
      v-if="!section.loading && section.items.length === 0"
      data-testid="training-matrix-document-section-no-results"
      class="mt-2 text-sm text-gray-05"
    >
      Nenhum resultado encontrado
    </p>
  </div>
</template>
